import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalendarPlus, faEdit, faTrash, faPlus, faCalendarDays, faBookmark as fasBookmark, faChevronDown, faChevronUp, faFilter } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as farBookmark } from '@fortawesome/free-regular-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAppointments } from '../../store/clinics/clinicsSlice';
import { clinicsService, type Appointment, type PatientTag } from '../../services/clinicsService';
import { clinicSchedulesService, type ClinicSchedule, type ClinicHoliday } from '../../services/clinicSchedulesService';
import chatService from '../../services/chatService';
import ClinicsNewAppointmentModal from './ClinicsNewAppointmentModal';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { GhostSwitch } from '../../components/ui/GhostSwitch';
import ClinicsAgendaCalendar from './ClinicsAgendaCalendar';
import ClinicsScheduleModal from './ClinicsScheduleModal';
import ClinicsHolidayModal from './ClinicsHolidayModal';
import ClinicsRoomModal from './ClinicsRoomModal';
import { SlidingPills } from '../../components/ui/SlidingPills';
import ClinicsAppointmentPopover from './ClinicsAppointmentPopover';
import ClinicsAppointmentsList from './ClinicsAppointmentsList';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { TagBadge } from '../../components/ui/TagBadge';
import { useNavigate } from 'react-router-dom';
import { getStatusColorDot, APPOINTMENT_STATUSES, getStatusLabel } from '../../utils/appointmentStatus';
import { useDeleteConfirmation } from '../../components/ui/DeleteConfirmationProvider';

const ClinicsAppointmentsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const requestDeleteConfirmation = useDeleteConfirmation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { appointments, totalAppointments, loading } = useAppSelector((state) => state.clinics);
  const isDocked = useAppSelector((state) => state.chat.isDocked);

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(!isDocked);

  useEffect(() => {
    setIsFiltersExpanded(!isDocked);
  }, [isDocked]);

  const [activeTab, setActiveTab] = useState<'agenda' | 'horarios' | 'festivos' | 'salas'>('agenda');
  
  // Agenda states
  const [page, setPage] = useState(1);
  const [agendaViewMode, setAgendaViewMode] = useState<'calendar' | 'list'>(() => {
    const saved = localStorage.getItem('clinics_agenda_viewMode');
    return (saved as 'calendar' | 'list') || 'calendar';
  });

  const handleAgendaViewModeChange = (id: string) => {
    setAgendaViewMode(id as 'calendar' | 'list');
    setPage(1);
    localStorage.setItem('clinics_agenda_viewMode', id);
  };
  const [tagsEnabled, setTagsEnabled] = useState(() => {
    const saved = localStorage.getItem('clinics_tags_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [pinnedFilters, setPinnedFilters] = useState(() => {
    const saved = localStorage.getItem('clinics_pinned_filters');
    return saved ? JSON.parse(saved) : { tags: false, status: false, room: false, dates: false };
  });

  const [tagFilter, setTagFilter] = useState<string[]>(() => {
    const pinned = localStorage.getItem('clinics_pinned_filters');
    const isPinned = pinned ? JSON.parse(pinned).tags : false;
    if (isPinned) {
      const val = localStorage.getItem('clinics_filter_tags');
      return val ? JSON.parse(val) : [];
    }
    return [];
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    const pinned = localStorage.getItem('clinics_pinned_filters');
    const isPinned = pinned ? JSON.parse(pinned).status : false;
    return isPinned ? (localStorage.getItem('clinics_filter_status') || '') : '';
  });
  const [roomFilter, setRoomFilter] = useState<string[]>(() => {
    const pinned = localStorage.getItem('clinics_pinned_filters');
    const isPinned = pinned ? JSON.parse(pinned).room : false;
    if (isPinned) {
      const val = localStorage.getItem('clinics_filter_room');
      try { return val ? JSON.parse(val) : []; } catch { return val ? [val] : []; }
    }
    return [];
  });
  const [startDate, setStartDate] = useState<string>(() => {
    const pinned = localStorage.getItem('clinics_pinned_filters');
    const isPinned = pinned ? JSON.parse(pinned).dates : false;
    return isPinned ? (localStorage.getItem('clinics_filter_startDate') || '') : '';
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const pinned = localStorage.getItem('clinics_pinned_filters');
    const isPinned = pinned ? JSON.parse(pinned).dates : false;
    return isPinned ? (localStorage.getItem('clinics_filter_endDate') || '') : '';
  });

  const updateTagFilter = (val: string[]) => {
    setTagFilter(val);
    setPage(1);
    if (pinnedFilters.tags) localStorage.setItem('clinics_filter_tags', JSON.stringify(val));
  };
  const updateStatusFilter = (val: string) => {
    setStatusFilter(val);
    setPage(1);
    if (pinnedFilters.status) localStorage.setItem('clinics_filter_status', val);
  };
  const updateRoomFilter = (val: string[]) => {
    setRoomFilter(val);
    setPage(1);
    if (pinnedFilters.room) localStorage.setItem('clinics_filter_room', JSON.stringify(val));
  };
  const updateStartDate = (val: string) => {
    setStartDate(val);
    setPage(1);
    if (pinnedFilters.dates) localStorage.setItem('clinics_filter_startDate', val);
  };
  const updateEndDate = (val: string) => {
    setEndDate(val);
    setPage(1);
    if (pinnedFilters.dates) localStorage.setItem('clinics_filter_endDate', val);
  };

  const togglePin = (filterName: 'tags' | 'status' | 'room' | 'dates') => {
    const isCurrentlyPinned = pinnedFilters[filterName];
    const newPinned = { ...pinnedFilters, [filterName]: !isCurrentlyPinned };
    setPinnedFilters(newPinned);
    localStorage.setItem('clinics_pinned_filters', JSON.stringify(newPinned));
    
    if (!isCurrentlyPinned) {
      if (filterName === 'tags') localStorage.setItem('clinics_filter_tags', JSON.stringify(tagFilter));
      if (filterName === 'status') localStorage.setItem('clinics_filter_status', statusFilter);
      if (filterName === 'room') localStorage.setItem('clinics_filter_room', JSON.stringify(roomFilter));
      if (filterName === 'dates') {
        localStorage.setItem('clinics_filter_startDate', startDate);
        localStorage.setItem('clinics_filter_endDate', endDate);
      }
    } else {
      if (filterName === 'tags') localStorage.removeItem('clinics_filter_tags');
      if (filterName === 'status') localStorage.removeItem('clinics_filter_status');
      if (filterName === 'room') localStorage.removeItem('clinics_filter_room');
      if (filterName === 'dates') {
        localStorage.removeItem('clinics_filter_startDate');
        localStorage.removeItem('clinics_filter_endDate');
      }
    }
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const perPage = agendaViewMode === 'list' ? 20 : 100;
  const totalPages = Math.max(1, Math.ceil(totalAppointments / perPage));

  // Popover state
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);

  // Schedules, Holidays and Rooms states
  const [schedules, setSchedules] = useState<ClinicSchedule[]>([]);
  const [holidays, setHolidays] = useState<ClinicHoliday[]>([]);
  const [rooms, setRooms] = useState<any[]>([]); // Using any or ClinicRoom
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [availableTags, setAvailableTags] = useState<PatientTag[]>([]);

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ClinicSchedule | null>(null);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<ClinicHoliday | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState<any>(null);

  const loadAppointments = useCallback(() => {
    const params: any = { page, per_page: perPage };
    if (tagFilter.length > 0) {
      params.tag_ids = tagFilter.join(',');
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (roomFilter.length === 1 && roomFilter[0] !== '') {
      params.room_id = roomFilter[0];
    }
    if (startDate) {
      params.date_from = startDate;
    }
    if (endDate) {
      params.date_to = endDate;
    }
    dispatch(fetchAppointments(params));
  }, [dispatch, page, perPage, tagFilter, statusFilter, roomFilter, startDate, endDate]);

  const loadConfigData = useCallback(async () => {
    setIsLoadingConfig(true);
    try {
      const [schedRes, holRes, roomsRes, tagsRes] = await Promise.all([
        clinicSchedulesService.getSchedules(),
        clinicSchedulesService.getHolidays(),
        clinicsService.getClinicRooms(),
        chatService.listTagsWithFolders()
      ]);
      setSchedules(schedRes);
      setHolidays(holRes);
      setRooms(roomsRes);
      setAvailableTags(tagsRes.tags as PatientTag[]);
    } catch (e) {
      console.error("Error loading schedules/holidays", e);
    } finally {
      setIsLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    loadAppointments();
    loadConfigData();
  }, [loadAppointments, loadConfigData]);

  const handleDelete = async (appointment: Appointment) => {
    const patientName = appointment.patient_name || `Paciente #${appointment.patient_id}`;
    if (!await requestDeleteConfirmation({
      title: 'Eliminar cita',
      message: <>
        ¿Estás seguro de que deseas eliminar la cita de <span className="font-bold">{patientName}</span>?<br /><br />
        Esta acción es irreversible y borrará cualquier histórico asociado a esta cita.
      </>,
    })) return;
    try {
      await clinicsService.deleteAppointment(appointment.id);
      loadAppointments();
    } catch (err) {
      alert('Error al eliminar la cita.');
    }
  };

  const handleTagsChange = async (appointmentId: number, tagIds: number[]) => {
    try {
      await clinicsService.updateAppointment(appointmentId, { tag_ids: tagIds });
      loadAppointments();
    } catch (err) {
      alert('Error al actualizar las etiquetas de la cita.');
    }
  };

  const handleStatusChange = async (appId: number, status: string) => {
    try {
      await clinicsService.updateAppointment(appId, { status });
      if (selectedAppointment && selectedAppointment.id === appId) {
        setSelectedAppointment({ ...selectedAppointment, status });
      }
      loadAppointments();
    } catch (err) {
      alert('Error al actualizar el estado de la cita.');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setIsLoadingConfig(true);
      await clinicSchedulesService.seedDefaults();
      await loadConfigData();
    } catch (e) {
      alert('Error al generar los horarios por defecto');
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    if (await requestDeleteConfirmation({ title: 'Eliminar horario', message: '¿Eliminar este horario?' })) {
      try {
        await clinicSchedulesService.deleteSchedule(id);
        loadConfigData();
      } catch (e) {
        alert('Error al eliminar horario');
      }
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    if (await requestDeleteConfirmation({ title: 'Eliminar día especial', message: '¿Eliminar este día especial?' })) {
      try {
        await clinicSchedulesService.deleteHoliday(id);
        loadConfigData();
      } catch (e) {
        alert('Error al eliminar día especial');
      }
    }
  };

  const handleSlotClick = (date: Date, room: any) => {
    setSelectedSlot(date);
    setSelectedRoomId(room.id);
    setIsModalOpen(true);
  };

  const handleAppointmentClick = (app: Appointment, e: React.MouseEvent) => {
    setSelectedAppointment(app);
    setPopoverPosition({ x: e.clientX, y: e.clientY });
  };

  const statusOptions = useMemo(() => [
    { value: '', label: t('clinics.appointments.allStates'), textLabel: t('clinics.appointments.allStates') },
    ...APPOINTMENT_STATUSES.map(status => ({
      value: status,
      label: (
        <div data-ui="clinics-appointments-page-region-001" className="flex items-center gap-2">
          <div data-ui="clinics-appointments-page-region-002" className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: getStatusColorDot(status) }} />
          <span>{getStatusLabel(status, t)}</span>
        </div>
      ),
      textLabel: getStatusLabel(status, t)
    }))
  ], []);

  const roomOptions = useMemo(() => [
    { value: '', label: t('clinics.appointments.onlyActive'), textLabel: t('clinics.appointments.onlyActive') },
    ...rooms.map(r => ({ 
      value: String(r.id), 
      label: (
        <div data-ui="clinics-appointments-page-region-003" className="flex items-center gap-2">
          <div data-ui="clinics-appointments-page-region-004" className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: r.color_code || '#808080' }} />
          <span className={!r.is_active ? 'opacity-50 line-through' : ''}>
            {r.name} {!r.is_active && <span className="text-xs ml-1">{i18n.language === 'en' ? '(inactive)' : '(inactiva)'}</span>}
          </span>
        </div>
      ),
      textLabel: `${r.name} ${!r.is_active ? (i18n.language === 'en' ? '(inactive)' : '(inactiva)') : ''}`
    }))
  ], [rooms, i18n.language]);

  const filteredAppointmentsList = useMemo(() => {
    return appointments.filter(app => {
      // In calendar view, hide cancelled by default unless explicitly searching for them
      if (agendaViewMode === 'calendar' && app.status === 'cancelled' && statusFilter !== 'cancelled') return false;
      if (patientSearch && (!app.patient_name || !app.patient_name.toLowerCase().includes(patientSearch.toLowerCase()))) return false;
      if (statusFilter && app.status !== statusFilter) return false;
      // Handle room filter
      const isSoloActivas = roomFilter.length === 0 || roomFilter.includes('');
      if (isSoloActivas) {
        const room = rooms.find(r => r.id === app.room_id);
        if (room && !room.is_active) return false;
      } else {
        if (!roomFilter.includes(String(app.room_id))) return false;
      }
      
      if (startDate || endDate) {
        const dateObj = new Date(app.start_time);
        const appDateStr = dateObj.toLocaleDateString('en-CA');
        if (startDate && appDateStr < startDate) return false;
        if (endDate && appDateStr > endDate) return false;
      }
      return true;
    });
  }, [appointments, agendaViewMode, patientSearch, statusFilter, roomFilter, rooms, startDate, endDate]);

  // --- Render Tabs ---

  const renderAgenda = () => {
    return (
      <div data-ui="clinics-appointments-page-region-005" className="flex flex-col gap-2 h-full min-h-0 animate-fade-in">
        <div data-ui="clinics-appointments-page-region-006" className="flex flex-col gap-3 bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-4 shrink-0 max-w-full overflow-hidden transition-all duration-300">
          {/* Top Line: SlidingPills, Nueva Cita button, and Top-Right Toggle Button */}
          <div data-ui="clinics-appointments-page-region-007" className="flex items-center justify-between gap-3 w-full">
            <div data-ui="clinics-appointments-page-region-008" className="flex flex-wrap items-center gap-3">
              <SlidingPills 
                options={[
                  { id: 'calendar', label: t('clinics.appointments.calendar') },
                  { id: 'list', label: t('clinics.appointments.list') }
                ]}
                activeId={agendaViewMode} 
                onChange={handleAgendaViewModeChange}
                variant="dark"
                size="md"
              />
              <BtnTaratic
                data-ui="clinics-new-appointment-button"
                onClick={() => { setSelectedSlot(null); setIsModalOpen(true); }}
                color="blue"
                size="md"
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <FontAwesomeIcon icon={faCalendarPlus} />
                {t('clinics.appointments.newAppointment')}
              </BtnTaratic>
            </div>

            <BtnTaratic
              color="ghost-dark"
              size="sm"
              onClick={() => setIsFiltersExpanded(prev => !prev)}
              className="flex items-center gap-1.5 text-xs shrink-0"
              aria-expanded={isFiltersExpanded}
              title={isFiltersExpanded ? (t('clinics.appointments.filters.hideFilters') || "Ocultar filtros") : (t('clinics.appointments.filters.showFilters') || "Filtros")}
            >
              <FontAwesomeIcon icon={faFilter} className="w-3.5 h-3.5" />
              <span className="hidden sm:inline font-medium">
                {isFiltersExpanded ? (t('clinics.appointments.filters.hideFilters') || 'Ocultar filtros') : (t('clinics.appointments.filters.showFilters') || 'Filtros')}
              </span>
              <FontAwesomeIcon icon={isFiltersExpanded ? faChevronUp : faChevronDown} className="w-3 h-3" />
            </BtnTaratic>
          </div>

          {/* Expandable Filters Section */}
          {isFiltersExpanded && (
            <div data-ui="clinics-appointments-page-region-009" className="flex flex-col gap-3 border-t border-white/10 pt-3 animate-fade-in">
              <div data-ui="clinics-appointments-page-region-010" className="flex flex-wrap items-center justify-between gap-3">
                <div data-ui="clinics-appointments-page-region-011" className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                  {tagsEnabled && (
                    <div data-ui="clinics-appointments-page-region-012" className="flex items-center gap-2">
                      <GhostSelect
                        value={tagFilter}
                        multiple={true}
                        onChange={(val: string[]) => updateTagFilter(val || [])}
                        placeholder={t('clinics.appointments.filters.all') || "Todos"}
                        className="w-full sm:w-64 max-w-full z-30"
                        options={[
                          ...availableTags.map(tag => ({
                            value: String(tag.id),
                            label: <TagBadge tag={tag} size="sm" />,
                            textLabel: tag.name,
                          }))
                        ]}
                      />
                      <BtnTaratic
                        data-ui="clinics-save-filter-button"
                        data-pinned={pinnedFilters.tags ? "true" : "false"}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin('tags'); }} 
                        color={pinnedFilters.tags ? "blue" : "ghost-dark"}
                        className="clinics-pinned-action-button w-7 h-7 flex items-center justify-center p-0 rounded-lg flex-shrink-0"
                        title="Fijar filtro"
                      >
                        <FontAwesomeIcon icon={pinnedFilters.tags ? fasBookmark : farBookmark} className="clinics-bookmark-icon text-sm" />
                      </BtnTaratic>
                    </div>
                  )}
                  <div data-ui="clinics-appointments-page-region-015" className="relative w-full sm:w-64 max-w-full">
                    <div data-ui="clinics-appointments-page-region-016" className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faSearch} className="text-white/70" />
                    </div>
                    <input
                      type="text"
                      placeholder={t('clinics.appointments.searchPatientPlaceholder')}
                      value={patientSearch}
                      onChange={(e) => {
                        setPatientSearch(e.target.value);
                        setPage(1);
                      }}
                      className="w-full bg-black/20 border border-white/20 text-white text-sm rounded-full focus:outline-none focus:ring-blue-400 focus:border-blue-400 block pl-11 p-2.5 placeholder-white/60 shadow-inner transition-all duration-300 hover:bg-black hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl focus:bg-black focus:shadow-inner-glow focus:shadow-inner-glow-white-2xl"
                    />
                  </div>
                </div>

                <div data-ui="clinics-tags-toggle">
                  <GhostSwitch
                    checked={tagsEnabled}
                    onChange={(checked) => {
                      setTagsEnabled(checked);
                      localStorage.setItem('clinics_tags_enabled', JSON.stringify(checked));
                    }}
                    label={t('clinics.appointments.useTags')}
                    variant="dark"
                    size="md"
                  />
                </div>
              </div>
              
              <div data-ui="clinics-appointments-page-region-017" className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 pt-3">
                <div data-ui="clinics-appointments-page-region-018" className="flex flex-wrap items-center gap-3">
                  <div data-ui="clinics-appointments-page-region-019" className="flex items-center gap-2 z-20">
                    <GhostSelect
                      options={statusOptions}
                      value={statusFilter}
                      onChange={(val) => updateStatusFilter(val)}
                      className="w-40 sm:w-48 max-w-full"
                      placeholder={t('clinics.appointments.allStates')}
                    />
                    <BtnTaratic
                      data-ui="clinics-save-filter-button"
                      data-pinned={pinnedFilters.status ? "true" : "false"}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin('status'); }} 
                      color={pinnedFilters.status ? "blue" : "ghost-dark"}
                        className="clinics-pinned-action-button w-7 h-7 flex items-center justify-center p-0 rounded-lg flex-shrink-0"
                      title="Fijar filtro"
                    >
                      <FontAwesomeIcon icon={pinnedFilters.status ? fasBookmark : farBookmark} className="clinics-bookmark-icon text-sm" />
                    </BtnTaratic>
                  </div>
                  
                  <div data-ui="clinics-appointments-page-region-020" className="flex items-center gap-2 z-10">
                    <GhostSelect
                      multiple={true}
                      options={roomOptions}
                      value={roomFilter}
                      onChange={(val) => updateRoomFilter(val)}
                      className="w-40 sm:w-48 max-w-full"
                      placeholder={t('clinics.appointments.onlyActive')}
                    />
                    <BtnTaratic
                      data-ui="clinics-save-filter-button"
                      data-pinned={pinnedFilters.room ? "true" : "false"}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin('room'); }} 
                      color={pinnedFilters.room ? "blue" : "ghost-dark"}
                        className="clinics-pinned-action-button w-7 h-7 flex items-center justify-center p-0 rounded-lg flex-shrink-0"
                      title="Fijar filtro"
                    >
                      <FontAwesomeIcon icon={pinnedFilters.room ? fasBookmark : farBookmark} className="clinics-bookmark-icon text-sm" />
                    </BtnTaratic>
                  </div>
                </div>

                <div data-ui="clinics-appointments-page-region-021" className="flex flex-wrap items-center gap-2 sm:ml-auto max-w-full">
                  <span className="text-sm text-white/60 whitespace-nowrap">{t('clinics.appointments.from')}</span>
                  <input 
                    type="date"
                    value={startDate}
                    onChange={(e) => updateStartDate(e.target.value)}
                    className="w-32 sm:w-36 text-xs sm:text-sm bg-black/20 border border-white/20 rounded-md text-white px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
                  />
                  <span className="text-sm text-white/60 whitespace-nowrap ml-1 sm:ml-2">{t('clinics.appointments.to')}</span>
                  <input 
                    type="date"
                    value={endDate}
                    onChange={(e) => updateEndDate(e.target.value)}
                    className="w-32 sm:w-36 text-xs sm:text-sm bg-black/20 border border-white/20 rounded-md text-white px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
                  />
                  <BtnTaratic
                    data-ui="clinics-save-filter-button"
                    data-pinned={pinnedFilters.dates ? "true" : "false"}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin('dates'); }} 
                    color={pinnedFilters.dates ? "blue" : "ghost-dark"}
                    className="clinics-pinned-action-button ml-1 w-7 h-7 shrink-0 rounded-lg flex items-center justify-center p-0"
                    title="Fijar rango de fechas"
                  >
                    <FontAwesomeIcon icon={pinnedFilters.dates ? fasBookmark : farBookmark} className="clinics-bookmark-icon w-4 h-4" />
                  </BtnTaratic>
                </div>
              </div>
            </div>
          )}
        </div>

        {loading || isLoadingConfig ? (
          <div data-ui="clinics-appointments-page-region-022" className="flex-1 flex justify-center items-center">
            <div data-ui="clinics-appointments-page-region-023" className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : (
          <div data-ui="clinics-appointments-page-region-024" className="flex-1 overflow-hidden">
            {agendaViewMode === 'calendar' ? (
              <ClinicsAgendaCalendar 
                rooms={rooms.filter(r => {
                  const isSoloActivas = roomFilter.length === 0 || roomFilter.includes('');
                  if (isSoloActivas) return r.is_active;
                  return roomFilter.includes(String(r.id));
                })}
                appointments={filteredAppointmentsList}
                schedules={schedules}
                holidays={holidays}
                startDate={startDate}
                endDate={endDate}
                onSlotClick={handleSlotClick}
                onAppointmentClick={handleAppointmentClick}
                onRoomClick={(room) => navigate(`/clinics/rooms/${room.id}/history`)}
              />
            ) : (
              <div data-ui="clinics-appointments-page-region-025" className="h-full min-h-0 overflow-hidden">
                <ClinicsAppointmentsList 
                  appointments={filteredAppointmentsList}
                  rooms={rooms}
                  onEdit={(app) => {
                    setAppointmentToEdit(app);
                    setIsModalOpen(true);
                  }}
                  onDelete={handleDelete}
                  onViewPatient={(patientId) => navigate(`/clinics/patients/${patientId}`)}
                  page={page}
                  totalPages={totalPages}
                  totalAppointments={totalAppointments}
                  perPage={perPage}
                  onPageChange={(newPage) => setPage(newPage)}
                  loading={loading}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderHorarios = () => {
    return (
      <div data-ui="clinics-appointments-page-region-026" className="space-y-6 animate-fade-in h-full overflow-y-auto pr-1">
        <div data-ui="clinics-appointments-page-region-027" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6">
          <div data-ui="clinics-appointments-page-region-028" className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Horarios de Clínica</h2>
            <div data-ui="clinics-appointments-page-region-029" className="flex gap-3">
              {schedules.length === 0 && (
                <BtnTaratic 
                  onClick={handleSeedDefaults} 
                  disabled={isLoadingConfig}
                  color="blue"
                  size="md"
                  className="flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Generar Horarios por Defecto
                </BtnTaratic>
              )}
              <BtnTaratic 
                onClick={() => { setSelectedSchedule(null); setIsScheduleModalOpen(true); }}
                color="ghost-dark"
                size="md"
                className="flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                Añadir Horario
              </BtnTaratic>
            </div>
          </div>
          
          <div data-ui="clinics-appointments-page-region-030" className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-white/60 bg-black/20 uppercase border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 font-medium">Día</th>
                  <th className="px-6 py-4 font-medium">Sala</th>
                  <th className="px-6 py-4 font-medium">Apertura</th>
                  <th className="px-6 py-4 font-medium">Cierre Clínica</th>
                  <th className="px-6 py-4 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr key={s.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 font-medium text-white capitalize">{['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'][s.day_of_week]}</td>
                    <td className="px-6 py-4 text-white/80">
                      {s.resource_id ? (() => {
                        const room = rooms.find(r => String(r.id) === String(s.resource_id));
                        return room ? (
                          <div data-ui="clinics-appointments-page-region-031" className="flex items-center gap-2">
                            <div data-ui="clinics-appointments-page-region-032" className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: room.color_code || '#808080' }} />
                            <span className={!room.is_active ? 'opacity-50 line-through' : ''}>
                              {room.name} {!room.is_active && <span className="text-[10px] ml-1">{i18n.language === 'en' ? '(inactive)' : '(inactiva)'}</span>}
                            </span>
                          </div>
                        ) : `Sala ${s.resource_id}`;
                      })() : '-'}
                    </td>
                    <td className="px-6 py-4 text-white/80">{s.start_time}</td>
                    <td className="px-6 py-4 text-white/80">{s.end_time}</td>
                    <td className="px-6 py-4">
                      <div data-ui="clinics-appointments-page-region-033" className="flex justify-end gap-2">
                        <button 
                          onClick={() => { setSelectedSchedule(s); setIsScheduleModalOpen(true); }} 
                          className="btn-taratic btn-ghost-dark btn-xs"
                        ><FontAwesomeIcon icon={faEdit} /></button>
                        <button 
                          onClick={() => handleDeleteSchedule(s.id!)}
                          className="btn-taratic btn-red btn-xs"
                        ><FontAwesomeIcon icon={faTrash} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {schedules.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-white/50">
                      No hay horarios configurados. El sistema creará los horarios por defecto para los nuevos tenants.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderFestivos = () => {
    return (
      <div data-ui="clinics-appointments-page-region-034" className="space-y-6 animate-fade-in h-full overflow-y-auto pr-1">
        <div data-ui="clinics-appointments-page-region-035" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6">
          <div data-ui="clinics-appointments-page-region-036" className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Días Especiales y Festivos</h2>
            <BtnTaratic 
              onClick={() => { setSelectedHoliday(null); setIsHolidayModalOpen(true); }}
              color="ghost-dark"
              size="md"
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Añadir Día Especial
            </BtnTaratic>
          </div>
          
          <div data-ui="clinics-appointments-page-region-037" className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-white/60 bg-black/20 uppercase border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 font-medium">Fecha</th>
                  <th className="px-6 py-4 font-medium">Cerrado</th>
                  <th className="px-6 py-4 font-medium">Horario Especial</th>
                  <th className="px-6 py-4 font-medium">Motivo / Descripción</th>
                  <th className="px-6 py-4 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((h) => {
                  const dateObj = new Date(h.fecha);
                  return (
                    <tr key={h.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 font-medium text-white">{dateObj.toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        {h.esta_abierto ? <span className="text-red-400">No</span> : <span className="text-green-400">Sí</span>}
                      </td>
                      <td className="px-6 py-4 text-white/80">
                        {h.esta_abierto && h.hora_apertura_especial && h.hora_cierre_especial 
                          ? `${h.hora_apertura_especial} - ${h.hora_cierre_especial}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-white/80">{h.descripcion || '-'}</td>
                      <td className="px-6 py-4">
                        <div data-ui="clinics-appointments-page-region-038" className="flex justify-end gap-2">
                          <button 
                            onClick={() => { setSelectedHoliday(h); setIsHolidayModalOpen(true); }}
                            className="btn-taratic btn-ghost-dark btn-xs"
                          ><FontAwesomeIcon icon={faEdit} /></button>
                          <button 
                            onClick={() => handleDeleteHoliday(h.id)}
                            className="btn-taratic btn-red btn-xs"
                          ><FontAwesomeIcon icon={faTrash} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSalas = () => {
    return (
      <div data-ui="clinics-appointments-page-region-039" className="space-y-6 animate-fade-in h-full overflow-y-auto pr-1">
        <div data-ui="clinics-appointments-page-region-040" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6">
          <div data-ui="clinics-appointments-page-region-041" className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Salas y Consultorios</h2>
            <BtnTaratic 
              onClick={() => { setRoomToEdit(null); setIsRoomModalOpen(true); }}
              color="ghost-dark"
              size="md"
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Añadir Sala
            </BtnTaratic>
          </div>
          
          <div data-ui="clinics-appointments-page-region-042" className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-white/60 bg-black/20 uppercase border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 font-medium">Color</th>
                  <th className="px-6 py-4 font-medium">Nombre</th>
                  <th className="px-6 py-4 font-medium">Estado</th>
                  <th className="px-6 py-4 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => (
                  <tr key={room.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div data-ui="clinics-appointments-page-region-043" className="w-6 h-6 rounded-full shadow-inner" style={{ backgroundColor: room.color_code }} />
                    </td>
                    <td className="px-6 py-4 font-medium text-white">{room.name}</td>
                    <td className="px-6 py-4">
                      {room.is_active ? (
                        <span data-ui="clinics-room-status-tag" className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-500/20 text-green-300">
                          Activa
                        </span>
                      ) : (
                        <span data-ui="clinics-room-status-tag" className="px-2.5 py-1 text-xs font-medium rounded-full bg-red-500/20 text-red-300">
                          Desactivada
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div data-ui="clinics-appointments-page-region-044" className="flex justify-end gap-2">
                        <button 
                          onClick={() => navigate(`/clinics/rooms/${room.id}/history`)}
                          className="btn-taratic btn-ghost-dark btn-xs"
                          title="Ver Historial"
                        ><FontAwesomeIcon icon={faCalendarDays} /></button>
                        <button 
                          onClick={() => { setRoomToEdit(room); setIsRoomModalOpen(true); }}
                          className="btn-taratic btn-ghost-dark btn-xs"
                          title="Editar Sala"
                        ><FontAwesomeIcon icon={faEdit} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-ui="clinics-appointments-page" className="page-theme-surface page-theme-clinics w-full dark-bg h-[calc(100vh-16px)] flex flex-col p-1 sm:p-2 overflow-hidden">
      <div data-ui="clinics-appointments-page-region-045" className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{t('clinics.appointments.title')}</h1>
        
        <SlidingPills 
          options={[
            { id: 'agenda', label: t('clinics.appointments.agenda') },
            { id: 'horarios', label: t('clinics.appointments.schedules') },
            { id: 'festivos', label: t('clinics.appointments.specialDays') },
            { id: 'salas', label: t('clinics.appointments.rooms') }
          ]} 
          activeId={activeTab} 
          onChange={(id) => setActiveTab(id as any)}
          variant="dark"
          size="md"
        />
      </div>

      <div data-ui="clinics-appointments-page-region-046" className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {activeTab === 'agenda' && renderAgenda()}
        {activeTab === 'horarios' && renderHorarios()}
        {activeTab === 'festivos' && renderFestivos()}
        {activeTab === 'salas' && renderSalas()}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <ClinicsNewAppointmentModal
          onClose={() => { setIsModalOpen(false); setAppointmentToEdit(null); setSelectedRoomId(null); }}
          onSuccess={() => {
            setIsModalOpen(false);
            setAppointmentToEdit(null);
            setSelectedRoomId(null);
            loadAppointments();
          }}
          initialDate={selectedSlot || undefined}
          initialRoomId={selectedRoomId || undefined}
          appointmentToEdit={appointmentToEdit || undefined}
          rooms={rooms}
        />
      )}

      {selectedAppointment && (
        <ClinicsAppointmentPopover
          appointment={selectedAppointment}
          position={popoverPosition}
          onClose={() => setSelectedAppointment(null)}
          onEdit={(app) => {
             setAppointmentToEdit(app);
             setIsModalOpen(true);
          }}
          onDelete={(app) => handleDelete(app)}
          onTagsChange={(appId, newTags) => handleTagsChange(appId, newTags)}
          onStatusChange={(appId, status) => handleStatusChange(appId, status)}
          availableTags={availableTags}
          tagsEnabled={tagsEnabled}
          onViewPatient={(patientId) => navigate(`/clinics/patients/${patientId}`)}
        />
      )}

      <ClinicsScheduleModal 
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSave={loadConfigData}
        scheduleToEdit={selectedSchedule}
        rooms={rooms}
      />

      <ClinicsHolidayModal
        isOpen={isHolidayModalOpen}
        onClose={() => setIsHolidayModalOpen(false)}
        onSave={loadConfigData}
        holidayToEdit={selectedHoliday}
      />

      {isRoomModalOpen && (
        <ClinicsRoomModal
          roomToEdit={roomToEdit}
          onClose={() => { setIsRoomModalOpen(false); setRoomToEdit(null); }}
          onSuccess={() => {
            setIsRoomModalOpen(false);
            setRoomToEdit(null);
            loadConfigData();
          }}
        />
      )}

    </div>
  );
};

export default ClinicsAppointmentsPage;
